import React, { ReactNode } from 'react';
import translate from '@translate';
import { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { ClaimType } from 'src/types/claim';

export type ExistingClaimSummary = Pick<ClaimType, 'ID' | 'FCN' | 'ESHOP_CLAIM_ID' | 'ESHOP_ORDER_ID'>;

/** Sentinel left after translate() replaces {{claimLinks}} – then swapped for React links. */
const CLAIM_LINKS_PLACEHOLDER = '<<claimLinks>>';

const getClaimDisplayLabel = (claim: ExistingClaimSummary): string => {
    const fcn = claim.FCN || (claim.ID != null ? `FCN-${claim.ID}` : '');
    const eshopClaimId = claim.ESHOP_CLAIM_ID || '';
    if (fcn && eshopClaimId) {
        return `${fcn} / ${eshopClaimId}`;
    }
    return fcn || eshopClaimId;
};

const renderClaimLinks = (claims: ExistingClaimSummary[]): ReactNode => {
    return claims.map((claim, index) => (
        <React.Fragment key={claim.ID}>
            {index > 0 && ', '}
            <a href={`/claims/${claim.ID}/`} target="_blank" rel="noopener noreferrer">
                {getClaimDisplayLabel(claim)}
            </a>
        </React.Fragment>
    ));
};

const renderMessageWithClaimLinks = (message: string, claims: ExistingClaimSummary[]): ReactNode => {
    const placeholderIndex = message.indexOf(CLAIM_LINKS_PLACEHOLDER);
    if (placeholderIndex === -1) {
        return message;
    }

    const beforeLinks = message.slice(0, placeholderIndex);
    const afterLinks = message.slice(placeholderIndex + CLAIM_LINKS_PLACEHOLDER.length);

    return (
        <>
            {beforeLinks}
            {renderClaimLinks(claims)}
            {afterLinks}
        </>
    );
};

export const buildClaimExistsDialogParams = (params: {
    orderId: string | number;
    eshopOrderId?: string | null;
    claims: ExistingClaimSummary[];
    canCreateMultiple: boolean;
    onConfirm?: () => void;
}): ConfirmDialogStatePropsType => {
    const { orderId, eshopOrderId, claims, canCreateMultiple, onConfirm } = params;
    const resolvedEshopOrderId = eshopOrderId || claims[0]?.ESHOP_ORDER_ID || '';
    const translateParams = {
        orderId,
        eshopOrderId: resolvedEshopOrderId,
        claimLinks: CLAIM_LINKS_PLACEHOLDER,
    };
    const messageKey = canCreateMultiple
        ? 'claimDetail.claimExistsConfirm'
        : 'claimDetail.claimExistsBlocked';

    return {
        title: translate('claimDetail.claimExistsTitle'),
        content: renderMessageWithClaimLinks(translate(messageKey, translateParams), claims),
        ...(canCreateMultiple
            ? {
                  action: onConfirm,
                  okLabel: 'claimDetail.action.create',
              }
            : {}),
    };
};
